import { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import PropTypes from 'prop-types';
import { BsArrowLeft, BsCheckCircleFill, BsXCircle, BsGlobe2, BsClipboardCheck, BsStars, BsX } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import IconButton from '#globalComponents/IconButton/IconButton';
import { useErrorStore } from '#lib/stores/errorStore';

import { prospectQueryKeys } from '../../../../leadOverview/models/prospectQueries/keys';
import { useAnalyzeCompany, useGeneratePipelineWithLeads } from '../../../models/onboarding/mutations';
import { usePipelineGenerationStatus, useOnboardingLeads } from '../../../models/onboarding/queries';
import AnalyzingView from '../../../pages/CrmOnboardingPage/AnalyzingView';
import AnalysisReviewStep from '../../../pages/CrmOnboardingPage/steps/AnalysisReviewStep';
import CompanyInputStep from '../../../pages/CrmOnboardingPage/steps/CompanyInputStep';

import OnboardingLeadsView from './OnboardingLeadsView';
import OnboardingProgress from './OnboardingProgress';

/**
 * Full-screen first-run onboarding modal — runs for every new user, 3 steps:
 *
 * 1. Website: user enters URL → AI crawls + analyzes (or enters profile manually)
 * 2. KI-Analyse prüfen: user reviews/edits analysis
 * 3. Strategie & Leads: shows the sales strategy + enriched leads
 *
 * Generates the strategy + leads only — no CRM pipeline is created here
 * (that requires the CRM add-on and happens in the CRM area).
 *
 * State machine: input → analyzing → reviewAnalysis → generating → success
 * The manual path (no website) jumps input → reviewAnalysis with empty fields.
 */

const STEPS = [
	{ key: 'website', label: 'Website', icon: BsGlobe2 },
	{ key: 'review', label: 'Firmenprofil prüfen', icon: BsClipboardCheck },
	{ key: 'results', label: 'Strategie & Leads', icon: BsStars },
];

const INITIAL_VALUES = {
	customerWebsite: '',
	websiteAnalysis: null,
};

const EMPTY_ANALYSIS = {
	branche: '',
	angebot: '',
	zielgruppe: '',
	preisniveau_indikator: 'mittel',
	usp: '',
	weitere_signale: '',
};

// After these thresholds the waiting screens offer a way out instead of an endless spinner
const ANALYSIS_STUCK_AFTER_S = 150;
const GENERATION_STUCK_AFTER_S = 180;

/**
 * Seconds elapsed since `key` last changed while `active` is true; 0 otherwise.
 * Drives the staged progress display and the stuck-state escape hatches.
 * @param {string} key - resets the timer whenever it changes
 * @param {boolean} active - whether the timer should run
 * @returns {number} elapsed seconds
 */
function useElapsedSeconds(key, active) {
	const [elapsed, setElapsed] = useState(0);
	useEffect(() => {
		setElapsed(0);
		if (!active) return undefined;
		const startedAt = Date.now();
		const id = setInterval(() => setElapsed(Math.round((Date.now() - startedAt) / 1000)), 1000);
		return () => clearInterval(id);
	}, [key, active]);
	return elapsed;
}

const FailureView = ({ title, message, primaryLabel, onPrimary, secondaryLabel, onSecondary }) => (
	<div className="space-y-4">
		<div className="flex items-start gap-3">
			<div className="h-10 w-10 rounded-control bg-warning-200 text-warning-700 flex items-center justify-center flex-shrink-0">
				<BsXCircle className="w-5 h-5" />
			</div>
			<div>
				<h2 className="text-lg font-semibold text-text mb-1">{title}</h2>
				<p className="text-sm text-bgBase-800">{message}</p>
			</div>
		</div>
		<div className="flex flex-col-reverse sm:flex-row justify-end gap-2">
			{secondaryLabel && (
				<Button variant="outline" onClick={onSecondary}>
					{secondaryLabel}
				</Button>
			)}
			<Button variant="solid" onClick={onPrimary}>
				{primaryLabel}
			</Button>
		</div>
	</div>
);

FailureView.propTypes = {
	title: PropTypes.string.isRequired,
	message: PropTypes.string.isRequired,
	primaryLabel: PropTypes.string.isRequired,
	onPrimary: PropTypes.func.isRequired,
	secondaryLabel: PropTypes.string,
	onSecondary: PropTypes.func,
};

const OnboardingModal = ({ isOpen, onClose }) => {
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const analyzeCompany = useAnalyzeCompany();
	const generateWithLeads = useGeneratePipelineWithLeads();
	const showError = useErrorStore((s) => s.showError);

	const [phase, setPhase] = useState('input');
	const [values, setValues] = useState(INITIAL_VALUES);
	const [generationId, setGenerationId] = useState(null);
	const [isManualProfile, setIsManualProfile] = useState(false);

	const { data: generation } = usePipelineGenerationStatus(generationId);

	const elapsedSec = useElapsedSeconds(phase, phase === 'analyzing' || phase === 'generating');

	// Determine which visual step we're on
	const currentStep = (() => {
		if (phase === 'input' || phase === 'analyzing') return 0;
		if (phase === 'reviewAnalysis') return 1;
		return 2; // generating, success
	})();

	// Strategy generation is done — show success immediately (leads load progressively)
	const isStrategyComplete = generation?.status === 'completed';

	// Lead search status for progressive display
	const isLeadSearchDone =
		generation?.leadSearchStatus === 'completed' ||
		generation?.leadSearchStatus === 'failed' ||
		generation?.leadSearchStatus === 'skipped';

	// Load leads progressively — SSE subscription while search is running, static fetch after
	const isLeadSearchRunning = generation?.leadSearchStatus === 'searching';
	const { data: leads = [] } = useOnboardingLeads(generationId, {
		enabled: isStrategyComplete && Boolean(generation?.prospectQueryId),
		prospectQueryId: generation?.prospectQueryId,
		isSearching: isLeadSearchRunning,
		isSearchDone: isLeadSearchDone,
	});

	// React to analysis completion
	useEffect(() => {
		if (phase === 'analyzing' && generation?.analysisStatus === 'completed') {
			setValues((prev) => ({ ...prev, websiteAnalysis: generation.websiteAnalysis }));
			setPhase('reviewAnalysis');
		}
	}, [phase, generation]);

	// React to strategy completion — show success immediately, don't wait for leads
	useEffect(() => {
		if (phase === 'generating' && isStrategyComplete) {
			setPhase('success');
		}
	}, [phase, isStrategyComplete]);

	const handleFieldChange = (patch) => setValues((prev) => ({ ...prev, ...patch }));

	// Step 1: Submit company for analysis
	const handleAnalyze = async () => {
		try {
			const res = await analyzeCompany.mutateAsync({
				customerWebsite: CompanyInputStep.getNormalizedWebsite(values),
			});
			setIsManualProfile(false);
			setGenerationId(res.generation?.id ?? null);
			setPhase('analyzing');
		} catch (err) {
			showError({ error: err, title: 'Analyse konnte nicht gestartet werden' });
		}
	};

	// Manual path: no website (or analysis failed) — fill in the profile by hand
	const handleManualEntry = () => {
		setIsManualProfile(true);
		setGenerationId(null);
		setValues((prev) => ({ ...prev, websiteAnalysis: prev.websiteAnalysis ?? { ...EMPTY_ANALYSIS } }));
		setPhase('reviewAnalysis');
	};

	// Step 2: Submit (edited) analysis → generate strategy + search leads.
	// Without a generationId the backend creates a fresh record (manual path).
	const handleGenerateWithLeads = async () => {
		try {
			const res = await generateWithLeads.mutateAsync({
				generationId,
				websiteAnalysis: values.websiteAnalysis,
			});
			if (res.generation?.id) setGenerationId(res.generation.id);
			setPhase('generating');
		} catch (err) {
			showError({ error: err, title: 'Konnte nicht gestartet werden' });
		}
	};

	// Ensure the /leads page refetches prospect queries on arrival, so it sees the
	// search created during onboarding instead of a stale/empty cache
	const goToLeads = () => {
		queryClient.invalidateQueries({ queryKey: prospectQueryKeys.all });
		onClose();
		navigate('/leads');
	};

	const handleViewLeads = goToLeads;

	const handleRetryAnalysis = () => {
		setGenerationId(null);
		setPhase('input');
	};

	const handleRetryGeneration = () => {
		setPhase('reviewAnalysis');
	};

	if (!isOpen) return null;

	// Render content based on phase

	const renderBody = () => {
		// Analyzing
		if (
			phase === 'analyzing' &&
			(!generation || generation.analysisStatus === 'pending' || generation.analysisStatus === 'analyzing')
		) {
			return (
				<div className="space-y-6">
					<AnalyzingView elapsedSec={elapsedSec} />
					{elapsedSec >= ANALYSIS_STUCK_AFTER_S && (
						<div className="flex flex-col items-center gap-2 animate-fadeIn">
							<p className="text-sm text-bgBase-800">
								Das dauert ungewöhnlich lange. Sie müssen nicht warten:
							</p>
							<div className="flex gap-2">
								<Button variant="outline" onClick={handleRetryAnalysis}>
									Erneut versuchen
								</Button>
								<Button variant="solid" onClick={handleManualEntry}>
									Profil selbst ausfüllen
								</Button>
							</div>
						</div>
					)}
				</div>
			);
		}

		// Analysis failed
		if (phase === 'analyzing' && generation?.analysisStatus === 'failed') {
			return (
				<FailureView
					title="Website-Analyse fehlgeschlagen"
					message="Das kann bei stark geschützten Websites passieren. Sie können es erneut versuchen — oder Ihr Firmenprofil in 30 Sekunden selbst ausfüllen, das Ergebnis ist dasselbe."
					primaryLabel="Profil selbst ausfüllen"
					onPrimary={handleManualEntry}
					secondaryLabel="Erneut versuchen"
					onSecondary={handleRetryAnalysis}
				/>
			);
		}

		// Generation failed (checked BEFORE the progress view — a failed status must
		// never leave the user on an endless spinner)
		if (phase === 'generating' && generation?.status === 'failed') {
			return (
				<FailureView
					title="Generierung fehlgeschlagen"
					message="Ihre Angaben sind gespeichert — versuchen Sie es einfach erneut."
					primaryLabel="Erneut versuchen"
					onPrimary={handleRetryGeneration}
					secondaryLabel="Zur Lead-Suche"
					onSecondary={goToLeads}
				/>
			);
		}

		// Generating — show progress until the strategy is done
		if (phase === 'generating' && !isStrategyComplete) {
			return (
				<div className="space-y-6">
					<OnboardingProgress generation={generation} elapsedSec={elapsedSec} />
					{elapsedSec >= GENERATION_STUCK_AFTER_S && (
						<div className="flex flex-col items-center gap-2 animate-fadeIn">
							<p className="text-sm text-bgBase-800">
								Das dauert ungewöhnlich lange. Sie müssen nicht warten:
							</p>
							<div className="flex gap-2">
								<Button variant="outline" onClick={handleRetryGeneration}>
									Erneut versuchen
								</Button>
								<Button variant="solid" onClick={goToLeads}>
									Zur Lead-Übersicht
								</Button>
							</div>
						</div>
					)}
				</div>
			);
		}

		// Success — show leads + strategy
		if (phase === 'success' && generation) {
			return (
				<OnboardingLeadsView
					generation={generation}
					leads={leads}
					isLeadSearchDone={isLeadSearchDone}
					onViewLeads={handleViewLeads}
				/>
			);
		}

		// Steps 1 + 2 (input / reviewAnalysis)
		const isStep1 = phase === 'input';

		return (
			<div>
				{isStep1 && (
					<CompanyInputStep values={values} onChange={handleFieldChange} onSkip={handleManualEntry} />
				)}
				{phase === 'reviewAnalysis' && (
					<AnalysisReviewStep values={values} onChange={handleFieldChange} isManual={isManualProfile} />
				)}
			</div>
		);
	};

	// Footer buttons for steps 1+2

	const renderFooter = () => {
		// Only the input + review steps own the footer
		if (phase === 'success' || phase === 'analyzing' || phase === 'generating') return null;

		const isStep1 = phase === 'input';
		const canProceed = isStep1 ? CompanyInputStep.isValid(values) : AnalysisReviewStep.isValid(values);

		return (
			<div className="flex items-center justify-between px-4 py-3 sm:px-8 sm:py-4 border-t border-bgBase-500 bg-bgBase-300 sm:rounded-b-card fixed bottom-0 left-0 right-0 sm:static z-10">
				<Button
					variant="outline"
					onClick={() => {
						if (phase === 'reviewAnalysis') setPhase('input');
						else onClose();
					}}
				>
					<BsArrowLeft className="h-4 w-4 sm:mr-2" />
					<span className="hidden sm:inline">{phase === 'reviewAnalysis' ? 'Zurück' : 'Abbrechen'}</span>
				</Button>
				<Button
					className="flex-1 sm:flex-none ml-3 sm:ml-0"
					variant="solid"
					onClick={isStep1 ? handleAnalyze : handleGenerateWithLeads}
					disabled={!canProceed}
					loading={analyzeCompany.isPending || generateWithLeads.isPending}
				>
					{isStep1 ? '✨ Website analysieren →' : '✨ Strategie & Leads erstellen →'}
				</Button>
			</div>
		);
	};

	return (
		<div className="fixed inset-0 bg-text/40 backdrop-blur-sm flex items-center justify-center z-50">
			<div className="bg-white w-full h-full flex flex-col relative sm:rounded-card sm:shadow-elevated sm:max-w-6xl sm:max-h-[92vh] sm:h-auto">
				<div className="px-4 pt-4 pb-3 sm:px-8 sm:pt-6 sm:pb-4 border-b border-bgBase-500">
					<div className="flex items-center justify-between mb-4">
						<div className="flex items-center gap-3">
							<div className="h-10 w-10 rounded-control bg-primary-100 text-primary-700 flex items-center justify-center">
								<BsStars className="w-5 h-5" />
							</div>
							<div>
								<h1 className="text-md sm:text-lg font-bold text-text">Willkommen bei B2B Hero</h1>
								<p className="text-xs sm:text-sm text-bgBase-800">
									In 3 Schritten zu Ihrer Vertriebsstrategie + ersten Leads
								</p>
							</div>
						</div>
						<div className="flex items-center gap-2">
							<span className="text-sm text-bgBase-800 hidden sm:inline">
								Schritt {currentStep + 1} von 3
							</span>
							<IconButton icon={BsX} label="Schließen" onClick={onClose} className="ml-2" />
						</div>
					</div>

					<div className="flex items-center gap-0">
						{STEPS.map((step, idx) => {
							const isCompleted = idx < currentStep;
							const isCurrent = idx === currentStep;
							const Icon = step.icon;

							return (
								<div key={step.key} className="flex items-center flex-1">
									<div className="flex items-center gap-2">
										<div
											className={`
											rounded-full flex items-center gap-1.5 text-sm font-medium transition-colors
											${isCurrent ? 'h-8 px-3 bg-primary-600 text-white' : ''}
											${isCompleted ? 'h-7 w-7 sm:h-8 sm:w-auto sm:px-3 justify-center sm:justify-start bg-success-200 text-success-700' : ''}
											${!isCurrent && !isCompleted ? 'h-7 w-7 sm:h-8 sm:w-auto sm:px-3 justify-center sm:justify-start bg-bgBase-400 text-bgBase-800' : ''}
										`}
										>
											{isCompleted ? (
												<BsCheckCircleFill className="w-4 h-4" />
											) : (
												<>
													<span className="font-bold">{idx + 1}</span>
													<Icon className="w-3.5 h-3.5 hidden sm:block" />
												</>
											)}
											<span className={`${isCurrent ? 'inline' : 'hidden'} sm:inline`}>
												{step.label}
											</span>
										</div>
									</div>
									{idx < STEPS.length - 1 && (
										<div
											className={`flex-1 h-0.5 mx-2 rounded ${isCompleted ? 'bg-success-500' : 'bg-bgBase-500'}`}
										/>
									)}
								</div>
							);
						})}
					</div>
				</div>

				<div className="flex-1 overflow-y-auto px-4 py-4 pb-24 sm:px-8 sm:py-6 sm:pb-6">{renderBody()}</div>

				{renderFooter()}
			</div>
		</div>
	);
};

OnboardingModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default OnboardingModal;
